import { isEqual, omit } from 'lodash'
import PropTypes from 'prop-types'
import { useState, useEffect, useRef, useCallback } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  ESTADO_PAUSADA,
  ESTADO_PUBLICADA,
  ESTADO_VENCIDA
} from '../../../constants/publicaciones.js'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { setModal } from '../../../slices/modalSlice'
import { formatearFecha } from '../../helpers/helper'
import { withValidador } from '../../hoc/withValidador'
import Metadatos from '../../publicaciones/Metadatos'
import AgregarFotos from '../../publicar/AgregarFotos'
import DescripcionProducto from '../../publicar/DescripcionProducto'
import FechaPublicacion from '../../publicar/FechaPublicacion'
import IndicarEtiquetas from '../../publicar/IndicarEtiquetas'
import ListaDeVentas from '../../publicar/ListaDeVentas'
import Ofertas from '../../publicar/Ofertas'
import VentasEspeciales from '../../publicar/VentasEspeciales'
import { GenericModal } from '../../reutilizables/modal/GenericModal'
import { actualizar } from '../../servicios/publicacionesService'
import * as publicacionService from '../../servicios/publicacionesService'
import LinkButton from '../../ui/LinkButton'
import Borde from './borde/Borde'
import CambioPrecio from './CambioPrecio'
import { getEsquema } from './esquema'
import Estado from './Estado'
import Exposicion from './Exposicion'

const FECHA = 10
const ENVIO_GRATIS = 1
const RETIRA_COMPRADOR = 2
const ENVIO_A_CARGO_COMPRADOR = 3
const CAMPOS_OFERTA = ['porcDcto', 'ofertaDesde', 'ofertaHasta']
const TIMEOUT = 200
const PASO = {
  PRINCIPIO: -1,
  VIGENCIA: 0,
  CAMBIO_PRECIO: 1,
  OFERTAS: 2,
  DESCRIPCION: 3,
  VENTAS_ESPECIALES: 4,
  ESTADO: 5
}

const AdministrarPublicacion = (props) => {
  const navigate = useNavigate()
  const [errores, setErrores] = useState({})
  const [grabando, setGrabando] = useState(false)
  const [publicacion, setPublicacion] = useState(props.publicacion)
  const [paso, setPaso] = useState(PASO.PRINCIPIO)
  const [hayCambios, setHayCambios] = useState(false)
  const [hayCambiosExternos, setHayCambiosExternos] = useState(false)
  const [accionEstado, setAccionEstado] = useState('PAUSAR_Y_REPUBLICAR')

  const dispatch = useDispatch()
  const vigenciaManualRef = useRef({
    desde: props.publicacion?.desde || '',
    hasta: props.publicacion?.hasta || ''
  })

  const pasoRefs = useRef({})
  const pasoRefSetters = useRef({})
  const ultimaActualizacion = useRef(publicacion)

  const getPasoElemento = useCallback((paso) => pasoRefs.current[paso] || null, [])

  const setPasoRef = useCallback((paso) => {
    if (!pasoRefSetters.current[paso]) {
      pasoRefSetters.current[paso] = (p) => {
        pasoRefs.current[paso] = p
      }
    }
    return pasoRefSetters.current[paso]
  }, [])

  const idVendedor = parseInt(localStorage.getItem('idUsuario'))
  const publicacionInicialRef = useRef(props.publicacion)

  useEffect(() => {
    ultimaActualizacion.current = publicacion
  }, [publicacion])

  useEffect(() => {
    if (Object.keys(props.publicacion).length > 0) {
      setPublicacion(props.publicacion)
      publicacionInicialRef.current = props.publicacion
      setHayCambios(false)
      const refInicio = getPasoElemento(PASO.PRINCIPIO)

      if (refInicio) {
        refInicio.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    }

    if (!Number(props.publicacion.idListaVenta)) {
      vigenciaManualRef.current = {
        desde: props.publicacion.desde || '',
        hasta: props.publicacion.hasta || ''
      }
    }
  }, [props.publicacion, getPasoElemento])

  const handleListaVentaChange = (listaSeleccionada) => {
    setPublicacion((prevState) => {
      if (listaSeleccionada && Number(listaSeleccionada.value) > 0) {
        if (!Number(prevState.idListaVenta)) {
          vigenciaManualRef.current = {
            desde: prevState.desde,
            hasta: prevState.hasta
          }
        }

        return {
          ...prevState,
          desde: listaSeleccionada.vigenciaDesde || prevState.desde,
          hasta: listaSeleccionada.vigenciaHasta || prevState.hasta
        }
      }

      return {
        ...prevState,
        desde: vigenciaManualRef.current.desde || prevState.desde,
        hasta: vigenciaManualRef.current.hasta || prevState.hasta
      }
    })
  }

  const pasoValido = useCallback(
    (name, value) => {
      const datosActualizados = {
        ...ultimaActualizacion.current,
        [name]: value
      }
      const datosParaValidarBase =
        value === undefined ? ultimaActualizacion.current : datosActualizados
      const datosParaValidar = {
        ...datosParaValidarBase,
        tieneRetiro: datosParaValidarBase.mediosEnvios?.some(
          (m) => m.idMedioEnvio === RETIRA_COMPRADOR
        ),
        tieneEnvioGratis: datosParaValidarBase.mediosEnvios?.some(
          (m) => m.idMedioEnvio === ENVIO_GRATIS
        )
      }

      const erroresValidacion = props.Validador.validarDato(getEsquema(), datosParaValidar, name)

      const requiereListaEnvio = datosParaValidar.mediosEnvios?.some(
        (medio) => medio.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
      )
      const errorListaEnvio =
        requiereListaEnvio && !datosParaValidar.idListaEnvio && !datosParaValidar.nombreListaEnvio
          ? 'Debes seleccionar una lista de envío para Envío a cargo del comprador.'
          : null

      setErrores((prev) => {
        const nuevos = { ...prev }

        if (!erroresValidacion || !erroresValidacion[name]) {
          delete nuevos[name]
        }

        if (!errorListaEnvio) {
          delete nuevos.idListaEnvio
        }

        return {
          ...nuevos,
          ...(erroresValidacion || {}),
          ...(errorListaEnvio ? { idListaEnvio: errorListaEnvio } : {})
        }
      })

      const hayErrores = Boolean(erroresValidacion) || Boolean(errorListaEnvio)
      return !hayErrores
    },
    [props.Validador]
  )

  const handleChangeState = (name, value, callBack) => {
    ultimaActualizacion.current = { ...ultimaActualizacion.current, [name]: value }
    setPublicacion((prevState) => ({
      ...prevState,
      [name]: value
    }))
    if (callBack) {
      callBack()
    }
  }

  const normalizarMedios = (medios, idField) =>
    (medios || []).map((m) => m[idField]).sort((a, b) => a - b)

  const normalizarPublicacion = (pub) => ({
    ...omit(pub, [
      'idPublicacionEstado',
      'hasta',
      'mediosPagos',
      'mediosEnvios',
      'garantias',
      'porcCambio',
      'importeCambio'
    ]),
    cantDisponible: Number(pub.cantDisponible) || 0,
    alicuota: Number(pub.alicuota) || 0,
    idListaVenta: pub.idListaVenta ? Number(pub.idListaVenta) : null,
    idListaEnvio: pub.idListaEnvio ? Number(pub.idListaEnvio) : null,
    nombreListaEnvio: pub.nombreListaEnvio || null,
    descZona: pub.descZona || null,
    descZonaRetiro: pub.descZonaRetiro || null,
    cantDiasEnvioGratis: pub.cantDiasEnvioGratis ?? null,
    cantDiasRetiroComprador: pub.cantDiasRetiroComprador ?? null,
    mediosPagos: normalizarMedios(pub.mediosPagos, 'idMedioPago'),
    mediosEnvios: normalizarMedios(pub.mediosEnvios, 'idMedioEnvio'),
    garantias: normalizarMedios(pub.garantias, 'idGarantia')
  })

  useEffect(() => {
    const hayDiferencias = !isEqual(
      normalizarPublicacion(publicacion),
      normalizarPublicacion(publicacionInicialRef.current)
    )
    const valorFinal = hayDiferencias || hayCambiosExternos
    setHayCambios(valorFinal)
  }, [publicacion, hayCambiosExternos])

  const obtenerValor = useCallback((value, valueCalculado, checked, type, tipoDato) => {
    let val
    switch (tipoDato) {
      case 'bool':
        val = value === 'true'
        break
      case 'num':
        val = Number(value)
        break
      case 'calc':
        val = valueCalculado
        break
      default:
        val = type === 'checkbox' || type === 'radio' ? checked : value
        break
    }
    return val
  }, [])

  const abrirModal = useCallback(
    (estadoNuevo, accionOverride) => {
      const accionEfectiva = accionOverride ?? accionEstado
      const nombreEstado =
        estadoNuevo === ESTADO_PAUSADA
          ? 'pausada'
          : estadoNuevo === ESTADO_VENCIDA
            ? 'vencida'
            : 'publicada'
      const TEXTOS = {
        ['UPDATE_SIMPLE']: {
          title: 'Cambio de estado',
          body: `Estás a punto de cambiar el estado de la publicación "${publicacion.titulo}" a ${nombreEstado}. 

          ${
            nombreEstado === 'vencida'
              ? 'La publicación cambiara de estado y la "Fecha de vigencia hasta" se actualizará automáticamente con la fecha actual.'
              : 'La publicación cambiara de estado y dejará de estar disponible para otros compradores.'
          }

          El resto de los datos de la publicación no se modificaran.

          ¿Estás seguro de que deseas continuar?`
        },
        ['PAUSAR_Y_REPUBLICAR']: {
          title: 'Aviso de pausa y publicación automática',
          body: `Como se han generado cambios, al guardar la publicación se pausará y se publicará automáticamente pasados los 30 minutos de la modificación. 
          ¿Desea continuar?`
        }
      }
      const { title, body } = TEXTOS[accionEfectiva] ?? TEXTOS['UPDATE_SIMPLE']
      dispatch(
        setModal({ size: 'sm', title, type: 'botones', body, show: true, modalId: 'cambio-estado' })
      )
    },
    [dispatch, publicacion, accionEstado]
  )

  const manejarCambioEstado = (esValido) => {
    pasoValido('idPublicacionEstado', ultimaActualizacion.current.idPublicacionEstado)

    const estabaPausada = publicacionInicialRef.current.idPublicacionEstado === ESTADO_PAUSADA

    const quierePublicar = ultimaActualizacion.current.idPublicacionEstado === ESTADO_PUBLICADA

    if (estabaPausada && quierePublicar && esValido && !hayCambios) {
      handleChangeState('idPublicacionEstado', publicacionInicialRef.current.idPublicacionEstado)
    }
  }

  const handleChange = useCallback(
    ({ currentTarget: input } = {}, tipoDato = '', valueCalculado = null) => {
      if (!input) {
        return
      }
      const { name, value, checked, type } = input
      const valorEfectivo = obtenerValor(value, valueCalculado, checked, type, tipoDato)
      handleChangeState(name, valorEfectivo, () => {
        const esValido = pasoValido(name, valorEfectivo)
        if (name === 'hasta') {
          manejarCambioEstado(esValido)
        }
      })
      if (name === 'idPublicacionEstado' && valorEfectivo !== ESTADO_PUBLICADA) {
        setAccionEstado('UPDATE_SIMPLE')
        abrirModal(valorEfectivo, 'UPDATE_SIMPLE')
      }
    },
    [pasoValido, handleChangeState, obtenerValor, hayCambios, hayCambiosExternos, abrirModal]
  )

  useEffect(() => {
    const refPaso = getPasoElemento(paso)
    if (refPaso) {
      refPaso.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [paso, getPasoElemento])

  const setErrorFechaPublicacion = () => {
    setErrores((prev) => ({
      ...prev,
      hasta: (
        <>
          Ups, la fecha de vigencia es inválida. Como la publicación se va a publicar
          automáticamente, la {'"Fecha Hasta"'} debe ser mayor o igual a la fecha actual.
        </>
      )
    }))
  }

  const confirmarCambioPublicacion = async () => {
    if (accionEstado === 'PAUSAR_Y_REPUBLICAR' && !pasoValido('hasta', publicacion.hasta)) {
      dispatch(setModal({ show: false }))
      setErrorFechaPublicacion()
      setPaso(PASO.VIGENCIA)
      setTimeout(() => {
        const ref = getPasoElemento(PASO.VIGENCIA)
        if (ref) {
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, TIMEOUT)
      return false
    }

    setGrabando(true)
    return guardarPublicacion(obtenerPublicacionActualizada())
  }

  const cancelarCambioPublicacion = () => {
    handleChangeState('idPublicacionEstado', publicacionInicialRef.current.idPublicacionEstado)
    setAccionEstado('PAUSAR_Y_REPUBLICAR')
    setErrores((prev) => ({
      ...prev,
      idPublicacionEstado: null
    }))
  }

  const guardarPublicacion = async (publicacionActualizada) => {
    try {
      await actualizar(publicacionActualizada)
      setGrabando(false)
      cancelarForm()
      return true
    } catch (error) {
      setGrabando(false)
      const mensaje = `Ocurrió un error al actualizar la publicación.`
      toastErrorLog(error, mensaje, 'publicaciones')
      return false
    }
  }

  const obtenerPublicacionActualizada = useCallback(() => {
    if (accionEstado === 'UPDATE_SIMPLE') {
      return {
        ...props.publicacion,
        idPublicacionEstado: publicacion.idPublicacionEstado,
        desde: formatearFecha(publicacion.desde, 'ISO'),
        hasta:
          publicacion.idPublicacionEstado === ESTADO_VENCIDA
            ? formatearFecha(new Date(), 'ISO')
            : formatearFecha(publicacion.hasta, 'ISO'),
        idListaVenta:
          publicacion.idPublicacionEstado === ESTADO_VENCIDA ? null : publicacion.idListaVenta
      }
    } else {
      return {
        ...omit(publicacion, CAMPOS_OFERTA),
        desde: formatearFecha(publicacion.desde, 'ISO'),
        hasta:
          publicacion.idPublicacionEstado === ESTADO_VENCIDA
            ? formatearFecha(new Date(), 'ISO')
            : formatearFecha(publicacion.hasta, 'ISO'),
        etiquetas: publicacion.etiquetas !== null ? publicacion.etiquetas : '',
        idPublicacionEstado:
          accionEstado === 'PAUSAR_Y_REPUBLICAR' ? ESTADO_PAUSADA : publicacion.idPublicacionEstado,
        modificaFechaReactivacion: accionEstado === 'PAUSAR_Y_REPUBLICAR'
      }
    }
  }, [publicacion, hayCambios, hayCambiosExternos])

  const validarForm = useCallback(async () => {
    const validacion = [
      { error: 'descripcion', paso: PASO.DESCRIPCION },
      { error: 'hasta', paso: PASO.VIGENCIA },
      { error: 'precioUnitario', paso: PASO.CAMBIO_PRECIO },
      { error: 'alicuota', paso: PASO.CAMBIO_PRECIO },
      { error: 'cantDisponible', paso: PASO.CAMBIO_PRECIO },
      { error: 'preciosMayorista', paso: PASO.CAMBIO_PRECIO },
      { error: 'mediosPagos', paso: PASO.VENTAS_ESPECIALES },
      { error: 'mediosEnvios', paso: PASO.VENTAS_ESPECIALES },
      { error: 'cantDiasEnvioGratis', paso: PASO.VENTAS_ESPECIALES },
      { error: 'cantDiasRetiroComprador', paso: PASO.VENTAS_ESPECIALES },
      { error: 'descZona', paso: PASO.VENTAS_ESPECIALES },
      { error: 'descZonaRetiro', paso: PASO.VENTAS_ESPECIALES },
      { error: 'idPublicacionEstado', paso: PASO.ESTADO }
    ]

    const estadoNuevo = publicacion.idPublicacionEstado

    const vaAPublicar = accionEstado === 'PAUSAR_Y_REPUBLICAR'

    const hayError = validacion.find((campo) => {
      return !pasoValido(campo.error, publicacion[campo.error])
    })

    if (hayError) {
      if (hayError.error === 'hasta' && vaAPublicar) {
        setErrorFechaPublicacion()
      }
      setPaso(hayError.paso)
      return
    }
    abrirModal(estadoNuevo)
  }, [
    pasoValido,
    publicacion,
    obtenerPublicacionActualizada,
    guardarPublicacion,
    hayCambios,
    hayCambiosExternos,
    abrirModal
  ])

  const claseActivo = (posicion) => paso === posicion

  const cancelarForm = () => {
    navigate('/Publicaciones/Administrar')
  }

  const aplicarDescuento = async (descuento, desde, hasta) => {
    const camposOferta = [
      { error: 'porcDcto', value: descuento },
      { error: 'ofertaDesde', value: desde },
      { error: 'ofertaHasta', value: hasta }
    ]
    const hayErrorOferta = camposOferta.some(({ error, value }) => !pasoValido(error, value))
    if (hayErrorOferta) {
      setPaso(PASO.OFERTAS)
      return
    }
    setGrabando(true)
    try {
      await publicacionService.ActualizarPublicacionIndividual({
        IdPublicacion: publicacion.idPublicacion,
        PrecioUnitario: publicacion.precioUnitario,
        IdMoneda: publicacion.idMoneda,
        OfertaDesde: desde,
        OfertaHasta: hasta,
        porcDcto: descuento
      })
      toast.success('Descuento aplicado correctamente')
    } catch (error) {
      const mensaje = `Error al aplicar descuento.`
      toastErrorLog(error, mensaje, 'publicaciones')
    }
    setGrabando(false)
  }

  const borrarDescuento = async () => {
    setGrabando(true)
    try {
      await publicacionService.ActualizarPublicacionIndividual({
        IdPublicacion: publicacion.idPublicacion,
        PrecioUnitario: publicacion.precioUnitario,
        IdMoneda: publicacion.idMoneda,
        OfertaDesde: null,
        OfertaHasta: null,
        porcDcto: null
      })
      toast.success('Descuento eliminado correctamente')
    } catch (error) {
      const mensaje = `Error al eliminar descuento.`
      toastErrorLog(error, mensaje, 'publicaciones')
    }
    setGrabando(false)
  }

  const fechaHastaFormateada = formatearFecha(publicacion.hasta, 'ISO')

  return (
    <>
      <form className="container-general" onSubmit={(e) => e.preventDefault()}>
        <div className="row" ref={setPasoRef(PASO.PRINCIPIO)}>
          <div className="col-12 row">
            <div className="col text-center">Items vendidos: {publicacion.totalItemsPedidos}</div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-4 borde-formulario">
              <Borde titulo="Exposición">
                <Exposicion
                  onChangeState={handleChangeState}
                  idExposicionAnterior={props.publicacion.idPublicacionExposicion}
                  idExposicionNueva={publicacion.idPublicacionExposicion}
                />
              </Borde>
            </div>
            <div className="col-sm-3 borde-formulario">
              <Borde
                titulo="Vigencia"
                error={errores.hasta}
                ref={setPasoRef(PASO.VIGENCIA)}
                activo={claseActivo(PASO.VIGENCIA)}
              >
                <FechaPublicacion
                  vieneDeAdministrar={true}
                  onChange={handleChange}
                  publicacion={publicacion}
                />
              </Borde>
            </div>
            <div className="col-sm-4 borde-formulario">
              <Borde titulo="Etiquetas">
                <IndicarEtiquetas
                  vieneDeAdministrar={true}
                  onChangeState={handleChangeState}
                  value={publicacion.etiquetas}
                />
              </Borde>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6 borde-formulario">
              <Borde titulo="Incluir en Lista de Ventas">
                <ListaDeVentas
                  vieneDeAdministrar={true}
                  idVendedor={idVendedor}
                  onChange={handleChange}
                  onChangeState={handleChangeState}
                  onListaVentaChange={handleListaVentaChange}
                  value={publicacion.idListaVenta ? publicacion.idListaVenta : 0}
                  publicacion={publicacion}
                />
              </Borde>
            </div>
            <div className="col-sm-5 borde-formulario">
              <Borde
                titulo="Estado Publicaciones"
                error={errores.idPublicacionEstado}
                ref={setPasoRef(PASO.ESTADO)}
                activo={claseActivo(PASO.ESTADO)}
              >
                <Estado
                  onChange={handleChange}
                  value={publicacion.idPublicacionEstado}
                  vigenciaHasta={fechaHastaFormateada?.slice(0, FECHA)}
                  fechaReactivacion={publicacion.fechaReactivacion}
                  estadoInicial={publicacionInicialRef.current.idPublicacionEstado}
                />
              </Borde>
            </div>
          </div>
          <div className="row">
            {publicacion.precioUnitario !== 0 && !publicacion.conPresupuesto && (
              <>
                <div className="col-sm-6 borde-formulario">
                  <Borde
                    titulo="Cambio de Precio"
                    error={errores.cantDisponible || errores.precioUnitario || errores.alicuota}
                    ref={setPasoRef(PASO.CAMBIO_PRECIO)}
                    activo={claseActivo(PASO.CAMBIO_PRECIO)}
                  >
                    <CambioPrecio
                      idPublicacion={publicacion.idPublicacion}
                      onChangeState={handleChangeState}
                      value={publicacion.precioUnitario}
                      monedaSimbolo={publicacion.monedaSimbolo}
                      alicuota={publicacion.alicuota}
                      precioMayorista={publicacion.preciosMayorista}
                      isDisabled={false}
                      cantDisponible={publicacion.cantDisponible}
                      onChange={handleChange}
                    />
                  </Borde>
                </div>
                <div className="col-sm-5 borde-formulario">
                  <Borde
                    titulo="Oferta"
                    error={errores.porcDcto || errores.ofertaDesde || errores.ofertaHasta}
                    ref={setPasoRef(PASO.OFERTAS)}
                    activo={claseActivo(PASO.OFERTAS)}
                  >
                    <Ofertas
                      vieneDeAdministrar={true}
                      values={{
                        porcDcto: publicacion.porcDcto ?? props.publicacion.porcDcto ?? 0,
                        porcDctoPublicacion:
                          publicacion.porcDctoPublicacion ??
                          props.publicacion.porcDctoPublicacion ??
                          0,
                        porcDctoLista:
                          publicacion.porcDctoLista ?? props.publicacion.porcDctoLista ?? 0,
                        precioUnitario:
                          publicacion.precioUnitario ?? props.publicacion.precioUnitario ?? 0,
                        ofertaDesde: publicacion.ofertaDesde ?? props.publicacion.ofertaDesde ?? '',
                        ofertaHasta: publicacion.ofertaHasta ?? props.publicacion.ofertaHasta ?? '',
                        listaVigenciaDesde:
                          publicacion.listaVigenciaDesde ??
                          props.publicacion.listaVigenciaDesde ??
                          '',
                        listaVigenciaHasta:
                          publicacion.listaVigenciaHasta ??
                          props.publicacion.listaVigenciaHasta ??
                          '',
                        listaOfertaDesde:
                          publicacion.listaOfertaDesde ?? props.publicacion.listaOfertaDesde ?? '',
                        listaOfertaHasta:
                          publicacion.listaOfertaHasta ?? props.publicacion.listaOfertaHasta ?? ''
                      }}
                      onChange={handleChange}
                    />
                    <div className="mt-2 d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success mr-2"
                        onClick={() =>
                          aplicarDescuento(
                            publicacion.porcDcto,
                            publicacion.ofertaDesde,
                            publicacion.ofertaHasta
                          )
                        }
                        disabled={
                          !publicacion.porcDcto ||
                          !publicacion.ofertaDesde ||
                          !publicacion.ofertaHasta
                        }
                      >
                        Aplicar descuento
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={borrarDescuento}
                        disabled={
                          publicacion.porcDctoPublicacion === 0 ||
                          publicacion.porcDctoPublicacion === null
                        }
                      >
                        Eliminar descuento
                      </button>
                    </div>
                  </Borde>
                </div>
              </>
            )}
          </div>
          <div className="row">
            <div className={`col-sm-6 borde-formulario `}>
              <Borde
                titulo="Escribí la descripción de tu producto"
                error={errores.descripcion}
                ref={setPasoRef(PASO.DESCRIPCION)}
                activo={claseActivo(PASO.DESCRIPCION)}
              >
                <DescripcionProducto onChange={handleChange} value={publicacion.descripcion} />
              </Borde>
            </div>
            <div className="col-sm-5 borde-formulario">
              <Borde titulo="Características del Producto">
                <Metadatos
                  idPublicacion={publicacion.idPublicacion}
                  editable={true}
                  onChange={handleChange}
                  onChangeExterno={(cambio) => setHayCambiosExternos(cambio)}
                />
              </Borde>
            </div>
          </div>
        </div>
        <div className="row">
          <div className={`col-sm-6 borde-formulario `}>
            <Borde titulo="Fotografías">
              <AgregarFotos
                value={publicacion.youtube || ''}
                vieneDeAdministrar={true}
                onChange={handleChange}
                idPublicacion={publicacion.idPublicacion}
                idPublicacionTemporal={0}
              />
            </Borde>
          </div>
          <div className="col-sm-5 borde-formulario">
            <Borde
              titulo="Método de Pago y Envío"
              ref={setPasoRef(PASO.VENTAS_ESPECIALES)}
              activo={claseActivo(PASO.VENTAS_ESPECIALES)}
              error={
                errores.mediosPagos ||
                errores.mediosEnvios ||
                errores.idListaEnvio ||
                errores.cantDiasEnvioGratis ||
                errores.cantDiasRetiroComprador ||
                errores.descZona ||
                errores.descZonaRetiro
              }
            >
              <VentasEspeciales
                vieneDeAdministrar={true}
                idPublicacion={publicacion.idPublicacion}
                values={{
                  extensionGarantia: publicacion.extensionGarantia,
                  garantias: publicacion.garantias,
                  mediosPagos: publicacion.mediosPagos,
                  mediosEnvios: publicacion.mediosEnvios,
                  envioGratis: publicacion.envioGratis,
                  descZona: publicacion.descZona,
                  descZonaRetiro: publicacion.descZonaRetiro,
                  cantDiasEnvioGratis: publicacion.cantDiasEnvioGratis,
                  cantDiasRetiroComprador: publicacion.cantDiasRetiroComprador,
                  idListaEnvio: publicacion.idListaEnvio,
                  nombreListaEnvio: publicacion.nombreListaEnvio
                }}
                onChange={handleChange}
                onAddGarantia={handleChangeState}
              />
            </Borde>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col">
            <div className="col d-flex justify-content-end">
              <LinkButton className="btn btn-volver-comprar w-50" onClick={validarForm}>
                {grabando ? (
                  <span>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="mr-2"
                    />
                    Guardando...
                  </span>
                ) : (
                  <span>
                    <i className="fa fa-check mr-2" aria-hidden="true" />
                    Aceptar
                  </span>
                )}
              </LinkButton>
            </div>
          </div>
          <div className="col">
            <LinkButton className="btn btn-reclamar w-50" onClick={cancelarForm}>
              <i className="fa fa-times mr-2" aria-hidden="true" />
              Cancelar
            </LinkButton>
          </div>
        </div>
        <GenericModal
          modalId="cambio-estado"
          onConfirm={confirmarCambioPublicacion}
          onClose={cancelarCambioPublicacion}
        />
      </form>
    </>
  )
}

AdministrarPublicacion.propTypes = {
  publicacion: PropTypes.shape({
    idPublicacionEstado: PropTypes.number.isRequired,
    cantDisponible: PropTypes.number.isRequired,
    idPublicacionExposicion: PropTypes.number.isRequired,
    idPublicacion: PropTypes.number.isRequired,
    titulo: PropTypes.string.isRequired,
    monedaSimbolo: PropTypes.string.isRequired,
    precioUnitario: PropTypes.number.isRequired,
    totalItemsPedidos: PropTypes.number.isRequired,
    publicacionEstado: PropTypes.string.isRequired,
    publicacionExposicion: PropTypes.string.isRequired,
    desde: PropTypes.string,
    hasta: PropTypes.string,
    etiquetas: PropTypes.string,
    idListaVenta: PropTypes.number,
    descripcion: PropTypes.string,
    youtube: PropTypes.string,
    extensionGarantia: PropTypes.number,
    garantias: PropTypes.array,
    mediosPagos: PropTypes.array,
    mediosEnvios: PropTypes.array,
    envioGratis: PropTypes.bool,
    porcDcto: PropTypes.number,
    porcDctoPublicacion: PropTypes.number,
    porcDctoLista: PropTypes.number,
    listaVigenciaDesde: PropTypes.string,
    listaVigenciaHasta: PropTypes.string,
    listaOfertaDesde: PropTypes.string,
    listaOfertaHasta: PropTypes.string,
    ofertaDesde: PropTypes.string,
    ofertaHasta: PropTypes.string,
    alicuota: PropTypes.number
  }).isRequired,
  idPublicacion: PropTypes.number.isRequired,
  setPublicacion: PropTypes.func.isRequired,
  Validador: PropTypes.shape({
    validarDato: PropTypes.func.isRequired
  }).isRequired
}

export default withValidador(AdministrarPublicacion)
